import { createTheme } from '@material-ui/core';
import { LabComponentNameToClassKey } from '@material-ui/lab/themeAugmentation';
import leftBorderImg from './assets/stepper-content-vertical-border.svg';
import leftBorderImgActive from './assets/stepper-content-vertical-border-active.svg';

declare module '@material-ui/core/styles/overrides' {
  interface ComponentNameToClassKey extends LabComponentNameToClassKey {}
}

declare module '@material-ui/core/styles/createPalette' {
  export interface ITypeBackground {
    secondary: string;
    table: string;
    tableHeader: string;
  }

  export interface TypeBackground extends ITypeBackground {}

  export interface CustomColors {
    [name: string]: string;
  }

  export interface Palette {
    background: TypeBackground;
    custom?: CustomColors;
  }

  export interface PaletteOptions {
    background?: Partial<TypeBackground>;
    custom?: CustomColors;
  }
}

export const palette = {
  primary: {
    main: '#000000',
    light: '#1C1C1C',
    dark: '#000000',
  },
  secondary: {
    main: '#00AAFF',
    light: '#73D0FF',
    dark: '#0084C6',
  },
  success: {
    main: '#34D092',
    light: '#85E3BE',
    dark: '#309164',
  },
  warning: {
    main: '#FD9656',
    light: '#FEC09A',
    dark: '#C17343',
  },
  error: {
    main: '#FD5959',
    light: '#FE9B9B',
    dark: '#B94949',
  },
  info: {
    main: '#F8F2E5',
    light: '#FFFFFF',
    dark: '#CDCAC4',
  },
  background: {
    default: '#F8F2E5',
    secondary: '#CDCAC4',
    table: '#FFFFFF',
    tableHeader: '#000000',
  },
  text: {
    primary: '#000000',
    secondary: '#1C1C1C',
    tableHeader: '#FFFFFF',
  } as any,
  common: {
    white: '#FFFFFF',
    black: '#000000',
  },
  custom: {
    border: '#CDCAC4',
    darkText: '#8F8A80',
    disabledText: '#ABA69C',
    lightBlack: '#333333',
  },
} as const;

const spacing = 4;
const borderRadius = 4;

export default createTheme({
  spacing,
  shape: {
    borderRadius,
  },
  typography: {
    fontFamily:
      "'Inter', 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  palette: palette as any,
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: {
          '& .seer-download-csv': {
            textDecoration: 'none',
          },
        },
      },
    },
    MuiStepper: {
      root: {
        padding: 0,
      },
    },
    MuiStep: {
      completed: {
        '& .MuiStepContent-root': {
          borderImage: `url(${leftBorderImg}) 0 0 0 5 repeat round`,
          borderLeft: '5px solid',
          marginLeft: 9,
          '&::before': {
            content: 'none',
          },
        },
      },
    },
    MuiStepLabel: {
      root: {
        fontSize: '1.5rem',
        color: palette.text.secondary,
      },
      iconContainer: {
        paddingRight: spacing * 10,
      },
    },
    MuiStepIcon: {
      root: {
        color: palette.custom.border,
        '&$active': {
          color: palette.secondary.main,
          '& $text': {
            fill: palette.common.white,
          },
        },
        '&$completed': {
          color: palette.success.main,
          '& $text': {
            fill: palette.common.white,
          },
        },
      },
      text: {
        fill: palette.text.secondary,
        fontSize: '0.6rem',
      },
    },
    MuiStepContent: {
      root: {
        marginTop: spacing * 5,
        paddingLeft: spacing * 13,
        position: 'relative',
        borderLeft: 0,
        '&::before': {
          content: '""',
          display: 'block',
          background: `url(${leftBorderImgActive}) no-repeat`,
          position: 'absolute',
          left: -3,
          top: 0,
          width: 5,
          height: '100%',
        },
      },
    },
    MuiStepConnector: {
      lineVertical: {
        minHeight: '0',
      },
    },
    MuiTabs: {
      indicator: {
        display: 'none',
      },
    },
    MuiTab: {
      root: {
        border: 0,
        textTransform: 'none',
        '@media (min-width: 600px)': {
          minWidth: 120,
        },
      },
      wrapper: {
        flexDirection: 'row',
      },
    },
    MuiTabPanel: {
      root: {
        padding: `${spacing * 4}px ${spacing * 8}px`,
        backgroundColor: palette.background.default,
        borderRadius: `0 ${spacing * 2}px ${spacing * 2}px`,
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: '0.875rem',
        color: palette.custom.darkText,
        '&$focused': {
          color: palette.secondary.main,
        },
        paddingBottom: spacing * 3,
      },
    },

    MuiInputBase: {
      input: {
        '&::placeholder': {
          color: palette.custom.disabledText,
          opacity: 1,
        },
      },
    },
    MuiFilledInput: {
      root: {
        borderRadius,
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: palette.common.white,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.custom.border,
        },
        '&$focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.secondary.main,
          borderWidth: 1,
        },
      },
      notchedOutline: {
        borderColor: palette.custom.border,
      },
      input: { color: palette.text.primary },
    },

    MuiFormHelperText: {
      root: {
        '&$error': {
          color: palette.error.main,
        },
      },
    },

    MuiSwitch: {
      root: {
        width: 40,
        height: 24,
        padding: 0,
        margin: `0 ${spacing}px`,
        overflow: 'unset',
      },
      switchBase: {
        padding: spacing,
        '&$checked': {
          transform: 'translateX(16px)',
          color: palette.common.white,
          '& + $track': {
            backgroundColor: palette.secondary.main,
            opacity: 1,
            border: 'none',
          },
          '& $thumb': {
            border: `1px solid ${palette.secondary.dark}`,
          },
        },
      },
      track: {
        borderRadius: 40,
        border: 'none',
        backgroundColor: palette.background.secondary,
        opacity: 1,
        boxSizing: 'border-box',
      },
      thumb: {
        boxShadow: 'none',
        backgroundColor: palette.common.white,
        border: `1px solid ${palette.custom.border}`,
        width: 16,
        height: 16,
        margin: -1,
      },
      checked: {},
    },
    MuiCard: {
      root: {
        borderRadius: borderRadius * 2,
      },
    },
    MuiCardActions: {
      root: {
        padding: spacing * 4,
        paddingTop: 0,
        justifyContent: 'flex-end',
      },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
        minWidth: 'auto',
      },
      containedSizeLarge: {
        padding: '10px',
      },
      outlinedSizeLarge: {
        padding: '9px',
      },
      startIcon: {
        marginRight: spacing * 4,
        marginLeft: 0,
      },
      outlined: {
        backgroundColor: palette.common.white,
        color: palette.common.black,
        border: `1px solid ${palette.common.black}`,
        '&:hover': {
          backgroundColor: palette.background.secondary,
        },
        '&:focus-visible': {
          outline: `2px solid ${palette.secondary.main}`,
        },
      },
      outlinedPrimary: {
        backgroundColor: palette.common.white,
        color: palette.common.black,
        border: `1px solid ${palette.common.black}`,
        '&:hover': {
          backgroundColor: palette.background.secondary,
        },
        '&:focus-visible': {
          outline: `2px solid ${palette.secondary.main}`,
        },
      },
      contained: {
        backgroundColor: palette.primary.main,
        color: palette.common.white,
        border: `1px solid ${palette.primary.main}`,
        '&:hover': {
          backgroundColor: palette.primary.light,
        },
        '&:focus-visible': {
          outline: `2px solid ${palette.secondary.main}`,
        },
        '&$disabled': {
          backgroundColor: palette.background.secondary,
          color: palette.custom.disabledText,
          borderColor: palette.background.secondary,
        },
        '& svg': {
          color: palette.common.white,
        },
      },
      containedPrimary: {
        backgroundColor: palette.primary.main,
        color: palette.common.white,
        border: `1px solid ${palette.primary.main}`,
        '&:hover': {
          backgroundColor: palette.primary.light,
        },
        '&:focus-visible': {
          outline: `2px solid ${palette.secondary.main}`,
        },
        '&$disabled': {
          backgroundColor: palette.background.secondary,
          color: palette.custom.disabledText,
          borderColor: palette.background.secondary,
        },
        '& svg': {
          color: palette.common.white,
        },
      },
      containedSecondary: {
        backgroundColor: palette.error.main,
        color: palette.common.white,
        border: `1px solid ${palette.error.dark}`,
        '&:hover': {
          backgroundColor: palette.error.dark,
        },
        '&:focus-visible': {
          outline: `2px solid ${palette.error.light}`,
        },
        '&.Mui-disabled': {
          backgroundColor: palette.background.secondary,
          color: palette.custom.disabledText,
          borderColor: palette.background.secondary,
        },
        '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
          color: palette.common.white,
        },
      },
    },
    MuiIconButton: {
      root: {
        borderRadius,
        padding: spacing * 2,
        color: palette.text.secondary,
        '&:hover, &:active': {
          color: palette.secondary.main,
          backgroundColor: 'transparent',
        },
        '& .MuiSvgIcon-root': {
          color: 'inherit',
        },
      },
    },
    MuiTableRow: {
      root: {
        '&$selected, &$selected:hover': {
          backgroundColor: palette.secondary.light,
          '& > .MuiTableCell': {
            color: palette.text.primary,
          },
        },
        '&:nth-of-type(odd)': {
          backgroundColor: palette.background.default,
        },
        '&:nth-of-type(even)': {
          backgroundColor: palette.common.white,
        },
        '&:hover': {
          backgroundColor: palette.background.secondary,
        },
      },
    },
    MuiTableCell: {
      root: {
        height: spacing * 10,
        padding: `${spacing * 2}px ${spacing * 3}px`,
        color: palette.text.primary,
        borderBottom: `1px solid ${palette.custom.border}`,
        borderLeft: `1px solid ${palette.custom.border}`,
        '&:first-child': {
          borderLeft: 0,
        },
      },
      head: {
        color: palette.text.tableHeader,
        fontWeight: 700,
        background: palette.background.tableHeader,
        '& .MuiTableSortLabel-root': {
          color: 'inherit',
        },
        '& .MuiTableSortLabel-root:hover': {
          color: 'inherit',
        },
        '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
          color: 'inherit',
        },
        '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
      },
      footer: {
        color: palette.common.black,
        fontWeight: 700,
        fontSize: '0.875rem',
      },
      stickyHeader: {
        backgroundColor: palette.background.tableHeader,
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: palette.text.primary,
        color: palette.common.white,
      },
      arrow: {
        color: palette.text.primary,
      },
    },
    MuiAlert: {
      standardInfo: {
        backgroundColor: palette.secondary.light,
        borderLeft: `4px solid ${palette.secondary.dark}`,
        color: palette.text.primary,
        '& .MuiAlert-icon': {
          color: palette.text.primary,
        },
      },
      outlinedInfo: {
        border: `1px solid ${palette.secondary.dark}`,
        color: palette.text.secondary,
        '& .MuiAlert-icon': {
          color: palette.text.secondary,
        },
      },
      filledInfo: {
        backgroundColor: palette.secondary.dark,
        color: palette.common.white,
      },

      outlinedSuccess: {
        border: `1px solid ${palette.success.main}`,
        color: palette.text.secondary,
        '& .MuiAlert-icon': {
          color: palette.success.main,
        },
      },
      filledSuccess: {
        backgroundColor: palette.success.main,
        color: palette.common.white,
      },

      standardSuccess: {
        backgroundColor: palette.success.light,
        borderLeft: `4px solid ${palette.success.main}`,
        color: palette.text.secondary,
        '& .MuiAlert-icon': {
          color: palette.text.secondary,
        },
      },

      outlinedWarning: {
        border: `1px solid ${palette.warning.main}`,
        color: palette.text.secondary,
        '& .MuiAlert-icon': {
          color: palette.warning.main,
        },
      },
      filledWarning: {
        backgroundColor: palette.warning.main,
        color: palette.common.white,
      },

      standardWarning: {
        backgroundColor: palette.warning.light,
        borderLeft: `4px solid ${palette.warning.main}`,
        color: palette.text.secondary,
        '& .MuiAlert-icon': {
          color: palette.text.secondary,
        },
      },

      outlinedError: {
        border: `1px solid ${palette.error.main}`,
        color: palette.text.secondary,
        '& .MuiAlert-icon': {
          color: palette.error.main,
        },
      },
      filledError: {
        backgroundColor: palette.error.main,
        color: palette.common.white,
      },

      standardError: {
        backgroundColor: palette.error.light,
        borderLeft: `4px solid ${palette.error.main}`,
        color: palette.text.secondary,
        '& .MuiAlert-icon': {
          color: palette.text.secondary,
        },
      },
    },
    MuiChip: {
      root: {
        borderRadius,
        backgroundColor: palette.custom.border,
        color: palette.text.secondary,
      },
      sizeSmall: {
        height: '1.75rem',
      },
      labelSmall: {
        fontSize: '0.75rem',
      },
    },
    MuiCheckbox: {
      root: {
        color: palette.text.secondary,
        '&$checked': {
          color: palette.secondary.main,
        },
      },
      colorPrimary: {
        '&$checked': {
          color: palette.secondary.main,
        },
      },
      colorSecondary: {
        '&$checked': {
          color: palette.primary.main,
        },
      },
    },
    MuiRadio: {
      root: {
        color: palette.background.secondary,
        '&$checked': {
          color: palette.secondary.main,
        },
      },
    },
    MuiSnackbarContent: {
      root: {
        '&.MuiAlert-standardInfo': {
          backgroundColor: palette.info.light,
          borderLeft: `4px solid ${palette.info.dark}`,
          color: palette.text.secondary,
          '& .MuiAlert-icon': {
            color: palette.text.secondary,
          },
        },
        '&.MuiAlert-standardSuccess': {
          backgroundColor: palette.success.light,
          borderLeft: `4px solid ${palette.success.main}`,
          color: palette.text.secondary,
          '& .MuiAlert-icon': {
            color: palette.text.secondary,
          },
        },
        '&.MuiAlert-standardWarning': {
          backgroundColor: palette.warning.light,
          borderLeft: `4px solid ${palette.warning.main}`,
          color: palette.text.secondary,
          '& .MuiAlert-icon': {
            color: palette.text.secondary,
          },
        },
        '&.MuiAlert-standardError': {
          backgroundColor: palette.error.light,
          borderLeft: `4px solid ${palette.error.main}`,
          color: palette.text.secondary,
          '& .MuiAlert-icon': {
            color: palette.text.secondary,
          },
        },
      },
    },
    MuiPaginationItem: {
      root: {
        color: palette.text.secondary,
        backgroundColor: palette.common.white,
        border: `1px solid ${palette.custom.disabledText}`,
        '&.MuiPaginationItem-ellipsis': {
          border: 'none',
          backgroundColor: 'transparent',
        },
        '&.Mui-selected': {
          backgroundColor: `${palette.primary.main} !important`,
          color: palette.common.white,
          borderColor: palette.primary.main,
          '&:hover': {
            backgroundColor: palette.primary.main,
          },
        },
        '&:not(.Mui-selected):hover': {
          backgroundColor: palette.background.secondary,
        },
      },
    },
  },
  props: {
    MuiTabs: {
      indicatorColor: 'primary',
    },
    MuiTextField: {
      variant: 'outlined',
      size: 'small',
    },
    MuiButton: {
      disableElevation: true,
    },
  },
});
